// src/semverCompare.ts
function isVersionBelow(current, min) {
  const currentParts = parseVersionParts(current);
  const minParts = parseVersionParts(min);
  if (!currentParts || !minParts) return false;
  for (let i = 0; i < Math.max(currentParts.length, minParts.length); i++) {
    const c = currentParts[i] ?? 0;
    const m = minParts[i] ?? 0;
    if (c !== m) return c < m;
  }
  return false;
}
function parseVersionParts(version) {
  const trimmed = version?.trim().replace(/^v/i, "");
  if (!trimmed) return void 0;
  const parts = trimmed.split(".");
  if (!parts.every((part) => /^\d+$/.test(part))) return void 0;
  return parts.map(Number);
}

// src/appStatusGates.ts
var DEFAULT_MAINTENANCE_MESSAGE = "Rock is temporarily unavailable while we perform maintenance. Please check back shortly.";
var DEFAULT_UPGRADE_MESSAGE = "A new version of Rock is required to continue. Please update the app.";
function resolveAppStatusGates(docs, ctx) {
  const gates = {};
  const banner = resolveAlertBanner(docs.alert);
  if (banner) {
    gates.banner = banner;
  }
  if (docs.maintenance?.enabled) {
    gates.maintenance = { message: docs.maintenance.message?.trim() || DEFAULT_MAINTENANCE_MESSAGE };
    return gates;
  }
  const minVersion = docs.requiredUpgrade?.enabled ? docs.requiredUpgrade.minVersion?.trim() : void 0;
  if (minVersion && ctx.platform !== "web" && isVersionBelow(ctx.nativeVersion, minVersion)) {
    gates.forceUpgrade = { message: docs.requiredUpgrade?.message?.trim() || DEFAULT_UPGRADE_MESSAGE, minVersion };
  }
  return gates;
}
function resolveAlertBanner(alert) {
  const message = alert?.enabled ? alert.message?.trim() : void 0;
  if (!message) return void 0;
  return { message, severity: alert?.severity ?? "warning" };
}

// src/assertUnreachable.ts
function assertUnreachable(_) {
  throw new Error("Didn't expect to get here");
}

// src/dateGrouping.ts
var DATE_GROUP_LABELS = ["Today", "Yesterday", "Last Week", "Last Month", "Last Year", "Older"];
function groupByDate(items, getDate, now = /* @__PURE__ */ new Date()) {
  const buckets = /* @__PURE__ */ new Map();
  for (const item of items) {
    const date = toDate(getDate(item));
    if (!date) continue;
    const group = bucketFor(date, now);
    const list = buckets.get(group);
    if (list) list.push(item);
    else buckets.set(group, [item]);
  }
  return DATE_GROUP_LABELS.map((group) => ({ group, items: buckets.get(group) ?? [] })).filter(
    (b) => b.items.length > 0
  );
}
function bucketFor(date, now) {
  const startOfToday = startOfDay(now);
  const startOfYesterday = addDays(startOfToday, -1);
  const startOfLast7 = addDays(startOfToday, -7);
  const startOfLast30 = addDays(startOfToday, -30);
  const startOfLast365 = addDays(startOfToday, -365);
  if (date >= startOfToday) return "Today";
  if (date >= startOfYesterday) return "Yesterday";
  if (date >= startOfLast7) return "Last Week";
  if (date >= startOfLast30) return "Last Month";
  if (date >= startOfLast365) return "Last Year";
  return "Older";
}
function startOfDay(d) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}
function addDays(d, days) {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}
function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// src/localDate.ts
import { LocalDateSchema } from "@rock/types";
function todayLocalDate(now = /* @__PURE__ */ new Date()) {
  return dateToLocalDate(now);
}
function dateToLocalDate(date) {
  return formatParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
}
function todayInTimeZone(now, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(now);
    const part = (type) => Number(parts.find((p) => p.type === type)?.value);
    return formatParts(part("year"), part("month"), part("day"));
  } catch {
    return dateToLocalDate(now);
  }
}
function toLocalDate(value) {
  return formatParts(...partsOf(value));
}
function parseLocalDate(date) {
  const [y, m, d] = partsOf(date);
  return new Date(y, m - 1, d);
}
function addDaysToLocalDate(date, days) {
  return shiftUtc(date, (d) => d.setUTCDate(d.getUTCDate() + days));
}
function addIntervalToLocalDate(date, interval) {
  const { value, unit } = interval;
  return shiftUtc(date, (d) => {
    switch (unit) {
      case "days":
        d.setUTCDate(d.getUTCDate() + value);
        break;
      case "weeks":
        d.setUTCDate(d.getUTCDate() + value * 7);
        break;
      case "months":
        d.setUTCMonth(d.getUTCMonth() + value);
        break;
      case "years":
        d.setUTCFullYear(d.getUTCFullYear() + value);
        break;
    }
  });
}
function formatLocalDateShort(date) {
  return parseLocalDate(date).toLocaleDateString(void 0, { month: "short", day: "numeric" });
}
function formatLocalDateMonthYear(date) {
  return parseLocalDate(date).toLocaleDateString(void 0, { month: "short", year: "numeric" });
}
function formatLocalDateWithWeekday(date, opts = {}) {
  return parseLocalDate(date).toLocaleDateString(void 0, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...opts.year && { year: "numeric" }
  });
}
function formatLocalDateLong(date, opts = {}) {
  return parseLocalDate(date).toLocaleDateString(void 0, {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...opts.year && { year: "numeric" }
  });
}
function daysBetweenLocalDates(a, b) {
  const [ay, am, ad] = partsOf(a);
  const [by, bm, bd] = partsOf(b);
  return Math.round((Date.UTC(ay, am - 1, ad) - Date.UTC(by, bm - 1, bd)) / MS_PER_DAY);
}
function compareLocalDates(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}
var MS_PER_DAY = 864e5;
function shiftUtc(date, mutate) {
  const [y, m, d] = partsOf(date);
  const utc = new Date(Date.UTC(y, m - 1, d));
  mutate(utc);
  return formatParts(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
}
function partsOf(date) {
  const [y, m, d] = date.slice(0, 10).split("-");
  return [Number(y), Number(m), Number(d)];
}
function formatParts(year, month, day) {
  const value = `${year}-${`${month}`.padStart(2, "0")}-${`${day}`.padStart(2, "0")}`;
  return LocalDateSchema.parse(value);
}

// src/dateSince.ts
var YEAR_REGEX = /^\d{4}$/;
var CALENDAR_DATE_REGEX = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/;
var MS_PER_DAY2 = 864e5;
function parseFlexibleDate(value) {
  if (value == null) return null;
  if (typeof value === "number" || typeof value === "string" && YEAR_REGEX.test(value)) {
    const year = Number(value);
    return isSupportedYear(year) ? new Date(year, 0, 1) : null;
  }
  if (typeof value === "string") {
    const calendar = CALENDAR_DATE_REGEX.exec(value);
    if (calendar) return parseCalendarDate(calendar);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}
function parseCalendarDate([, yearText, monthText, dayText]) {
  const year = Number(yearText);
  const month = Number(monthText);
  const day = dayText ? Number(dayText) : 1;
  if (!isSupportedYear(year) || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const lastDayOfMonth = new Date(year, month, 0).getDate();
  return new Date(year, month - 1, Math.min(day, lastDayOfMonth));
}
function isSupportedYear(year) {
  return year >= 1e3 && year <= 9999;
}
function computeTimeSince(value, unit, now) {
  return computeTimeSinceDay(value, unit, dateToLocalDate(now ?? /* @__PURE__ */ new Date()));
}
function computeTimeSinceDay(value, unit, today) {
  const date = parseFlexibleDate(value);
  if (!date) return null;
  const ref = parseLocalDate(today);
  switch (unit) {
    case "years": {
      let years = ref.getFullYear() - date.getFullYear();
      const anniversaryPassed = ref.getMonth() > date.getMonth() || ref.getMonth() === date.getMonth() && ref.getDate() >= date.getDate();
      if (!anniversaryPassed) years--;
      return years;
    }
    case "months": {
      let months = (ref.getFullYear() - date.getFullYear()) * 12 + (ref.getMonth() - date.getMonth());
      if (ref.getDate() < date.getDate()) months--;
      return months;
    }
    case "days": {
      const refDay = Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate());
      const dateDay = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
      return Math.round((refDay - dateDay) / MS_PER_DAY2);
    }
  }
}

// src/considerationDemandClass.ts
import { isWithheldStatus } from "@rock/types";
function considerationDemandClass(input) {
  if (isActNowCriticality(input.criticality)) return "act-now";
  if (input.taskTypes.some((tt) => tt.subscription?.type === "recurring")) return "handled";
  if (hasActionableTaskTypes(input.taskTypes)) return "check";
  return void 0;
}
function hasActionableTaskTypes(taskTypes) {
  return taskTypes.some((tt) => !!tt.subscription);
}
function isActionableConsideration(input) {
  return input.taskTypes.length > 0 && !!considerationDemandClass(input);
}
function considerationCadence(taskTypes) {
  const kinds = new Set(liveTaskTypes(taskTypes).flatMap((tt) => tt.subscription ? [tt.subscription.type] : []));
  if (kinds.size > 1) return "mixed";
  return kinds.has("recurring") ? "recurring" : kinds.has("one-time") ? "one-time" : void 0;
}
function liveTaskTypes(taskTypes) {
  return taskTypes.filter((taskType) => !isWithheldStatus(taskType.documentStatus));
}
function isActNowCriticality(criticality) {
  return criticality === "critical" || criticality === "urgent";
}

// src/considerationSchedule.ts
function isScheduledForLater(consideration, activeThrough) {
  return consideration.status === "pending" && !!consideration.reviewAt && consideration.reviewAt > activeThrough;
}
function isQueued(consideration) {
  return consideration.status === "pending" && !consideration.reviewAt && !isActNowCriticality(consideration.criticality);
}
function isPendingThisWeek(consideration, activeThrough) {
  if (consideration.status !== "pending") return false;
  if (!consideration.reviewAt) return isActNowCriticality(consideration.criticality);
  return consideration.reviewAt <= activeThrough;
}
function isUnreadThisWeek(consideration, activeThrough) {
  return consideration.read !== true && isPendingThisWeek(consideration, activeThrough);
}
function sortScheduledForLaterLast(items, activeThrough) {
  const open = [];
  const scheduled = [];
  for (const item of items) {
    if (isScheduledForLater(item, activeThrough)) scheduled.push(item);
    else open.push(item);
  }
  scheduled.sort((a, b) => compareLocalDates(a.reviewAt, b.reviewAt));
  return [...open, ...scheduled];
}
function isAnsweredSince(consideration, since) {
  if (consideration.status !== "accepted" && consideration.status !== "ignored") return false;
  return !!consideration.reviewedAt && consideration.reviewedAt >= since;
}
function isOnThisWeek(consideration, activeThrough, weekStart) {
  if (consideration.status !== "pending") return isAnsweredSince(consideration, weekStart);
  return isPendingThisWeek(consideration, activeThrough);
}
function resolveReviewStamp(nowIso) {
  return { reviewedAt: nowIso };
}
function isPushedBackByOwner(consideration) {
  return consideration.status === "pending" && !!consideration.snoozedAt;
}

// src/dedupeStrings.ts
function dedupeStrings(values) {
  const unique = /* @__PURE__ */ new Set();
  for (const value of values) {
    if (typeof value === "string" && value.length > 0) {
      unique.add(value);
    }
  }
  return Array.from(unique);
}

// src/uniqueById.ts
function uniqueById(items) {
  return [...new Map([...items].map((item) => [item.id, item])).values()];
}

// src/localDateRange.ts
function isLocalDateInRange(day, range) {
  return day >= range.start && day <= range.end;
}
function weekBoundsToLocalDateRange(bounds) {
  return toLocalDateRange(bounds.startOfWeek, bounds.endOfWeek);
}
function toLocalDateRange(start, end) {
  return { start: dateToLocalDate(start), end: dateToLocalDate(end) };
}

// src/manufactureDate.ts
var MONTHS = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  SEPT: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12
};
var MONTH_NAME = "(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|SEPT|OCT|NOV|DEC)[A-Z]*";
var MIN_YEAR = 1950;
var MAX_YEAR = 2099;
function parseManufactureDate(raw) {
  const value = raw.toUpperCase().replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
  if (!value) return void 0;
  let m = /^(\d{4})[-/ ](\d{1,2})(?:[-/ ](\d{1,2}))?$/.exec(value);
  if (m && isYear(m[1]) && isMonth(m[2])) return { year: Number(m[1]), month: Number(m[2]) };
  m = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
  if (m && isYear(m[1]) && isMonth(m[2]) && Number(m[3]) >= 1 && Number(m[3]) <= 31) {
    return { year: Number(m[1]), month: Number(m[2]) };
  }
  m = /^(\d{1,2})[-/](\d{4})$/.exec(value);
  if (m && isYear(m[2]) && isMonth(m[1])) return { year: Number(m[2]), month: Number(m[1]) };
  m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(value);
  if (m && isYear(m[3])) {
    const first = Number(m[1]);
    const second = Number(m[2]);
    if (isMonth(m[1]) && (!isMonth(m[2]) || first === second)) return { year: Number(m[3]), month: first };
    if (isMonth(m[1]) || isMonth(m[2])) return { year: Number(m[3]) };
    return void 0;
  }
  m = new RegExp(`^(\\d{4}) ?${MONTH_NAME}(?: ?\\d{1,2})?$`).exec(value);
  if (m && isYear(m[1])) return { year: Number(m[1]), month: MONTHS[m[2]] };
  m = new RegExp(`^(?:\\d{1,2} ?)?${MONTH_NAME} ?(?:\\d{1,2} ?)?(\\d{4})$`).exec(value);
  if (m && isYear(m[2])) return { year: Number(m[2]), month: MONTHS[m[1]] };
  m = /^(\d{4})$/.exec(value);
  if (m && isYear(m[1])) return { year: Number(m[1]) };
  return void 0;
}
function isYear(value) {
  const year = Number(value);
  return !!value && value.length === 4 && year >= MIN_YEAR && year <= MAX_YEAR;
}
function isMonth(value) {
  const month = Number(value);
  return !!value && month >= 1 && month <= 12;
}

// src/cleanFirestoreId.ts
function cleanFirestoreId(input) {
  if (!input) return "_";
  return input.normalize("NFKD").replace(/[^\w.:-]/g, "-").replace(/^-+|-+$/g, "").substring(0, 1500);
}

// src/detectCodeLabels.ts
var MODEL_LABEL_RE = /\b(?:model|m\s*\/\s*no?|mod|cat(?:alog)?)\b/i;
var SERIAL_LABEL_RE = /\b(?:serial|s\s*\/\s*no?|ser)\b/i;
function detectCodeLabels(text) {
  const found = [];
  if (MODEL_LABEL_RE.test(text)) found.push("model-number");
  if (SERIAL_LABEL_RE.test(text)) found.push("serial-number");
  return found;
}

// src/dimensionValue.ts
function getDimensionFieldCount(subType) {
  if (subType === "length" || subType === "sqft") {
    return 1;
  }
  if (subType === "LxW") {
    return 2;
  }
  return 3;
}
var DimensionValueUtil = class _DimensionValueUtil {
  /**
   * Parse a dimension value string (e.g., "20x30 ft", "20x30x40 ft", "100 ft²")
   * Always parses values and unit uniformly - extracts numbers and x's as values, then the unit.
   * @param value - The value string to parse
   * @param subType - The dimension sub type
   * @param defaultUnit - Default unit to use if not found in value
   * @returns Parsed value with parts array and unit, or null if invalid
   */
  static parseDimension(value, subType, defaultUnit = "ft") {
    if (!value) {
      return null;
    }
    const expectedParts = getDimensionFieldCount(subType);
    const isSqft = subType === "sqft";
    let parts = [];
    let unit = defaultUnit;
    const match = value.match(/^([0-9.x]+)\s*([a-zA-Z]+)?(²|\^2)?$/);
    if (match?.[1]) {
      const dimensionsPart = match[1];
      parts = dimensionsPart.split("x").map((p) => p.trim());
      const parsedUnit = match[2];
      if (parsedUnit) {
        if (parsedUnit === "sqft") {
          unit = "ft";
        } else if (["ft", "in", "cm", "m"].includes(parsedUnit)) {
          unit = parsedUnit;
        }
      }
    } else {
      if (value.includes("x")) {
        parts = value.split("x").map((p) => p.trim());
      } else {
        parts = value.split(/[^0-9.]+/).filter((p) => p);
      }
    }
    if (isSqft) {
      unit = "ft";
    }
    if (parts.length === 0 || parts.length > expectedParts) {
      return null;
    }
    let calculatedValue;
    const validParts = parts.map((p) => parseFloat(p || "0")).filter((num) => !Number.isNaN(num) && num > 0);
    if (validParts.length === expectedParts && validParts.length > 0) {
      const product = validParts.reduce((acc, val) => acc * val, 1);
      let exponent;
      if (subType === "sqft") {
        exponent = 2;
      } else if (subType === "length") {
        exponent = 1;
      } else {
        exponent = validParts.length;
      }
      calculatedValue = {
        value: product,
        unit,
        exponent
      };
    }
    return {
      parts,
      unit,
      calculatedValue
    };
  }
  /**
   * Concatenate dimension parts with unit for storage.
   * Stores values as "N values separated by X's, space, unit"
   * Exception: sqft stores with ft² (hardcoded)
   * @param parts - Array of dimension parts (e.g., ["20", "30"] or ["100"] for single dimension)
   * @param unit - The unit to append
   * @param subType - The dimension sub type
   * @returns Formatted string like "20x30 ft" or "100 ft²" for sqft
   */
  static concatDimension(parts, unit, subType) {
    if (!parts || parts.length === 0) {
      return "";
    }
    const expectedParts = getDimensionFieldCount(subType);
    const isSqft = subType === "sqft";
    const isSingleDimension = subType === "length" || isSqft;
    const cleanedParts = parts.map((p) => {
      const cleaned = p.replace(/[^0-9.]/g, "");
      if (cleaned === "." || cleaned.endsWith(".") && cleaned.length > 1) {
        return cleaned;
      }
      if (cleaned === "0" || cleaned.startsWith("0.")) {
        return cleaned;
      }
      const num = parseFloat(cleaned);
      return Number.isNaN(num) || num < 0 ? "" : cleaned;
    });
    while (cleanedParts.length < expectedParts) {
      cleanedParts.push("");
    }
    if (cleanedParts.length > expectedParts) {
      cleanedParts.splice(expectedParts);
    }
    const hasValues = cleanedParts.some((p) => {
      if (!p) return false;
      if (p.endsWith(".")) return true;
      if (p === "0" || p.startsWith("0.")) return true;
      const num = parseFloat(p);
      return !Number.isNaN(num) && num >= 0;
    });
    if (!hasValues) {
      return "";
    }
    let dimensionsStr;
    if (isSingleDimension) {
      dimensionsStr = cleanedParts[0] || "";
    } else {
      const partsToJoin = [...cleanedParts];
      while (partsToJoin.length > 0 && partsToJoin[partsToJoin.length - 1] === "") {
        partsToJoin.pop();
      }
      if (partsToJoin.length === 0) {
        return "";
      }
      dimensionsStr = partsToJoin.join("x");
    }
    if (isSqft) {
      return `${dimensionsStr} ft\xB2`;
    }
    return `${dimensionsStr} ${unit}`;
  }
  /**
   * Format a number as a square feet dimension value (e.g., 3719 → "3719 ft²").
   */
  static formatSquareFeet(value) {
    return _DimensionValueUtil.concatDimension([`${value}`], "ft", "sqft");
  }
  /**
   * Update a single part of a dimension value
   * @param currentValue - Current full dimension value string
   * @param partIndex - Index of the part to update (0, 1, or 2)
   * @param newPartValue - New value for that part (can be partial like "10.")
   * @param unit - Current unit
   * @param subType - The dimension sub type
   * @returns Updated dimension value string
   */
  static updateDimensionPart(currentValue, partIndex, newPartValue, unit, subType) {
    const expectedParts = getDimensionFieldCount(subType);
    const parsed = _DimensionValueUtil.parseDimension(currentValue, subType, unit);
    if (!parsed) {
      const parts = Array(expectedParts).fill("");
      parts[partIndex] = newPartValue;
      return _DimensionValueUtil.concatDimension(parts, unit, subType);
    }
    const updatedParts = [...parsed.parts];
    while (updatedParts.length < expectedParts) {
      updatedParts.push("");
    }
    if (updatedParts.length > expectedParts) {
      updatedParts.splice(expectedParts);
    }
    updatedParts[partIndex] = newPartValue;
    return _DimensionValueUtil.concatDimension(updatedParts, parsed.unit, subType);
  }
  /**
   * Format a dimension value for display (outside of editing screens).
   * - If renderCalculatedValue is true: displays calculated value (e.g., "30x20 ft" → "600 ft²")
   * - For others: displays "N values separated by X's, unit" (unit NOT squared)
   * - For sqft: displays with ft² (as stored)
   * @param value - The dimension value string to format
   * @param subType - The dimension sub type
   * @param defaultUnit - Default unit to use if not found in value
   * @param renderCalculatedValue - Whether to render the calculated value with unit squared
   * @returns Formatted display string, or original value if parsing fails
   */
  static formatDisplay(value, subType, defaultUnit = "ft", renderCalculatedValue = false) {
    if (!value) {
      return "";
    }
    const valueWithSuperscript = value.replace(/\^2/g, "\xB2");
    const parsed = _DimensionValueUtil.parseDimension(valueWithSuperscript, subType, defaultUnit);
    if (!parsed || parsed.parts.length === 0) {
      return valueWithSuperscript;
    }
    const { parts, unit, calculatedValue } = parsed;
    const isSqft = subType === "sqft";
    const isSingleDimension = subType === "length" || isSqft;
    const formatNumber = (num) => {
      return num % 1 === 0 ? num.toString() : num.toFixed(2).replace(/\.?0+$/, "");
    };
    const cleanedParts = parts.map((p) => {
      const cleaned = p.replace(/[^0-9.]/g, "");
      const num = parseFloat(cleaned);
      if (Number.isNaN(num) || num <= 0) {
        return "";
      }
      return isSingleDimension ? formatNumber(num) : num.toString();
    }).filter((p) => p);
    if (cleanedParts.length === 0) {
      return "";
    }
    if (renderCalculatedValue && calculatedValue && calculatedValue.value > 0) {
      const valueDisplay = formatNumber(calculatedValue.value);
      const exponentStr = calculatedValue.exponent === 1 ? "" : calculatedValue.exponent === 2 ? "\xB2" : calculatedValue.exponent === 3 ? "\xB3" : `^${calculatedValue.exponent}`;
      return `${valueDisplay} ${calculatedValue.unit}${exponentStr}`;
    }
    if (isSqft) {
      const dimensionsStr2 = cleanedParts[0];
      return `${dimensionsStr2} ft\xB2`;
    }
    const dimensionsStr = isSingleDimension ? cleanedParts[0] : cleanedParts.join("x");
    return `${dimensionsStr} ${unit}`;
  }
};

// src/equipmentIdentity.ts
import { EQUIPMENT_CORE_FIELD_KEYS } from "@rock/types";
var LABELS = {
  "model-number": { dataKey: EQUIPMENT_CORE_FIELD_KEYS.MODEL_NUMBER, label: "Model Number", shortLabel: "Model" },
  "product-name": { dataKey: EQUIPMENT_CORE_FIELD_KEYS.PRODUCT_NAME, label: "Product Name", shortLabel: "Product" }
};
function equipmentIdentity(equipment) {
  const modelNumber = equipment.fields?.modelNumber;
  const productName = equipment.fields?.productName;
  const kind = modelNumber ? "model-number" : productName ? "product-name" : void 0;
  if (!kind) return void 0;
  const typed = kind === "model-number" ? `${modelNumber}` : `${productName}`;
  return { kind, ...LABELS[kind], typed, display: equipment.details?.modelDisplay || typed };
}
function equipmentModelNumber(equipment) {
  const identity = equipmentIdentity(equipment);
  return identity?.kind === "model-number" ? identity.display : void 0;
}
function hideProductNameBesideModelNumber(fields, hasValue2) {
  const showsModelNumber = fields.some((field) => field.dataKey === EQUIPMENT_CORE_FIELD_KEYS.MODEL_NUMBER);
  if (!showsModelNumber || hasValue2(EQUIPMENT_CORE_FIELD_KEYS.PRODUCT_NAME)) return fields;
  return fields.filter((field) => field.dataKey !== EQUIPMENT_CORE_FIELD_KEYS.PRODUCT_NAME);
}

// src/evaluateFieldDisplay.ts
function evaluateFieldDisplay(fieldDisplay, coreFields, blockTypeFields, explicitMap) {
  const allFields = [...blockTypeFields ?? [], ...coreFields ?? []];
  let shownFields = [];
  let hiddenFields = [];
  const displayType = fieldDisplay.display;
  switch (displayType) {
    case "all-fields": {
      shownFields = allFields;
      hiddenFields = [];
      break;
    }
    case "core-only": {
      shownFields = coreFields ?? [];
      hiddenFields = blockTypeFields ?? [];
      break;
    }
    case "custom-only": {
      shownFields = blockTypeFields ?? [];
      hiddenFields = coreFields ?? [];
      break;
    }
    case "show-except": {
      const hiddenFieldKeys = fieldDisplay.hiddenFields.map((f) => f.dataKey);
      shownFields = allFields.filter((f) => !hiddenFieldKeys.includes(f.dataKey));
      hiddenFields = allFields.filter((f) => hiddenFieldKeys.includes(f.dataKey));
      break;
    }
    case "hide-except": {
      const shownFieldKeys = fieldDisplay.shownFields.map((f) => f.dataKey);
      shownFields = allFields.filter((f) => shownFieldKeys.includes(f.dataKey));
      hiddenFields = allFields.filter((f) => !shownFieldKeys.includes(f.dataKey));
      break;
    }
    default:
      assertUnreachable(displayType);
  }
  if (explicitMap !== void 0) {
    const finalShown = [];
    const finalHidden = [];
    for (const f of allFields) {
      if (Object.hasOwn(explicitMap, f.dataKey)) {
        if (explicitMap[f.dataKey] === "show") {
          finalShown.push(f);
        } else if (explicitMap[f.dataKey] === "hide") {
          finalHidden.push(f);
        }
      } else if (shownFields.some((sf) => sf.dataKey === f.dataKey)) {
        finalShown.push(f);
      } else {
        finalHidden.push(f);
      }
    }
    shownFields = finalShown;
    hiddenFields = finalHidden;
  }
  return { shownFields, hiddenFields };
}

// src/filterNonNull.ts
function filterNonNull(value) {
  return value !== null && value !== void 0;
}

// src/firestoreError.ts
var TRANSIENT_READ_CODES = ["unavailable", "deadline-exceeded"];
function firestoreErrorCode(err) {
  const info = errInfo(err);
  return info.code ?? info.codeFromMessage;
}
function isFirestoreCode(err, code) {
  const actual = firestoreErrorCode(err);
  return typeof actual === "string" && actual.endsWith(code);
}
function isTransientReadError(err) {
  return TRANSIENT_READ_CODES.some((code) => isFirestoreCode(err, code));
}
function errInfo(err) {
  if (err === null) return { type: "null" };
  if (err === void 0) return { type: "undefined" };
  if (typeof err !== "object") return { type: typeof err, str: String(err) };
  const e = err;
  const code = typeof e.code === "string" ? e.code : void 0;
  const messageStr = typeof e.message === "string" ? e.message : void 0;
  const codeFromMessage = messageStr?.match(/^\s*\[([^\]]+)\]/)?.[1];
  return {
    type: "object",
    ctor: e.constructor?.name,
    name: e.name,
    code,
    codeFromMessage,
    message: e.message,
    nativeErrorCode: e.nativeErrorCode,
    userInfo: e.userInfo,
    cause: e.cause,
    keys: Object.keys(e),
    str: safeToString(err)
  };
}
function safeToString(err) {
  try {
    const s = String(err);
    return s.length > 500 ? `${s.slice(0, 500)}...` : s;
  } catch {
    return void 0;
  }
}

// src/formatGuideValue.ts
function formatGuideValue(value, options) {
  if (value === void 0 || value === null) {
    return "";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return `${value}`;
  }
  if (typeof value === "string") {
    return resolveOptionTitle(value, options);
  }
  if (Array.isArray(value)) {
    return value.map((v) => resolveOptionTitle(v, options)).join(", ");
  }
  return "";
}
function resolveOptionTitle(value, options) {
  return options?.find((o) => o.value === value)?.title ?? value;
}

// src/timeZone.ts
var FALLBACK_TIME_ZONE = "America/New_York";
function knownTimeZone(timeZone) {
  if (!timeZone) return FALLBACK_TIME_ZONE;
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone }).resolvedOptions().timeZone;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}
function homeTimeZone(home) {
  return knownTimeZone(home?.geo?.timezone);
}
function localDayStartIso(day, timeZone) {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  const date = Number(day.slice(8, 10));
  return localWallClockToIso(year, month, date, 0, timeZone);
}
function localWallClockToIso(year, month, day, hour, timeZone) {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, 0, 0, 0);
  const guess = naiveUtc - getTimezoneOffsetMs(naiveUtc, timeZone);
  return new Date(naiveUtc - getTimezoneOffsetMs(guess, timeZone)).toISOString();
}
function nextLocalSendAt(args) {
  const { from, timeZone, targetDayOfWeek, hour } = args;
  const today = getLocalParts(from, timeZone);
  let daysUntil = (targetDayOfWeek - today.dayOfWeek + 7) % 7;
  if (daysUntil === 0 && today.hour >= hour) {
    daysUntil = 7;
  }
  const targetLocal = addLocalDays(today, daysUntil);
  return localWallClockToIso(targetLocal.year, targetLocal.month, targetLocal.day, hour, timeZone);
}
function getLocalParts(instant, timeZone) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit"
  });
  const parts = fmt.formatToParts(instant);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  let hour = Number(get("hour"));
  if (hour === 24) hour = 0;
  const weekday = get("weekday");
  const dayOfWeek = WEEKDAY_INDEX[weekday] ?? 0;
  return { year, month, day, hour, dayOfWeek };
}
function makeLocalAnchorDate(parts) {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, 0, 0, 0));
}
var WEEKDAY_INDEX = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};
function addLocalDays(parts, days) {
  const anchor = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  anchor.setUTCDate(anchor.getUTCDate() + days);
  return {
    year: anchor.getUTCFullYear(),
    month: anchor.getUTCMonth() + 1,
    day: anchor.getUTCDate(),
    hour: parts.hour,
    dayOfWeek: anchor.getUTCDay()
  };
}
function getTimezoneOffsetMs(utcMs, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).formatToParts(new Date(utcMs));
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  let hour = get("hour");
  if (hour === 24) hour = 0;
  const asIfUtc = Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
  return asIfUtc - utcMs;
}

// src/weekUtil.ts
var DEFAULT_WEEKEND_START = 6;
var DEFAULT_WEEK_CONFIG = {
  weekendStart: DEFAULT_WEEKEND_START,
  weekStart: (DEFAULT_WEEKEND_START + 2) % 7
};
var DAYS_OF_WEEK = [
  { value: 0, short: "Sun", long: "Sunday" },
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" }
];
function homeWeekConfig(home) {
  const weekendStart = home?.weekendStart ?? DEFAULT_WEEKEND_START;
  return {
    weekendStart,
    weekStart: (weekendStart + 2) % 7
  };
}
function nextWeekendStartLocalDate(today, weekendStartDay) {
  if (!Number.isInteger(weekendStartDay) || weekendStartDay < 0 || weekendStartDay > 6) {
    throw new RangeError(`nextWeekendStartLocalDate: weekendStartDay must be 0\u20136, got ${weekendStartDay}`);
  }
  const daysUntil = (weekendStartDay - parseLocalDate(today).getDay() + 7) % 7 || 7;
  return addDaysToLocalDate(today, daysUntil);
}
function weekendAtLeastDaysOut(today, days, weekendStartDay) {
  return nextWeekendStartLocalDate(addDaysToLocalDate(today, days - 1), weekendStartDay);
}
function weekendOnOffer(today, weekendStartDay) {
  if (parseLocalDate(today).getDay() === weekendStartDay) return today;
  return nextWeekendStartLocalDate(today, weekendStartDay);
}
function weekRangeContaining(day, weekendStartDay) {
  const weekStartDay = (weekendStartDay + 2) % 7;
  const start = addDaysToLocalDate(day, -((parseLocalDate(day).getDay() - weekStartDay + 7) % 7));
  return { start, end: addDaysToLocalDate(start, 6) };
}
function toUserDay(stdDay, config) {
  return (stdDay - config.weekStart + 7) % 7;
}
function userWeekendStartDay(config) {
  return (config.weekendStart - config.weekStart + 7) % 7;
}
var WeekUtil = class _WeekUtil {
  // ============================================================================
  // Core date utilities - work with any date
  // ============================================================================
  /**
   * Gets the start of the week for any given date.
   * @param date Any date within the week
   * @param config Week configuration
   * @returns The start of that week at 00:00:00 local time
   */
  static getStartOfWeek(date, config) {
    const ud = toUserDay(date.getDay(), config);
    const result = new Date(date);
    result.setDate(date.getDate() - ud);
    result.setHours(0, 0, 0, 0);
    return result;
  }
  /**
   * Gets the end of the week for any given date.
   * The week end is always userDay 6 — the last of the 7 days in the week.
   * @param date Any date within the week
   * @param config Week configuration
   * @returns The end of that week at 23:59:59.999 local time
   */
  static getEndOfWeek(date, config) {
    const ud = toUserDay(date.getDay(), config);
    const result = new Date(date);
    result.setDate(date.getDate() + (6 - ud));
    result.setHours(23, 59, 59, 999);
    return result;
  }
  /**
   * Gets the week bounds (week start to week end) for any given date.
   * @param date Any date within the week
   * @param config Week configuration
   * @returns Object with startOfWeek and endOfWeek dates
   */
  static getWeekBounds(date, config) {
    return {
      startOfWeek: _WeekUtil.getStartOfWeek(date, config),
      endOfWeek: _WeekUtil.getEndOfWeek(date, config)
    };
  }
  /**
   * Checks if the given date falls on a weekend day.
   *
   * The weekend is the tail of the user week: every day from weekendStart
   * through to the end of the 7-day week.  Weekend length is therefore
   * fully derived from weekStart and weekendStart — no separate field needed.
   *
   * e.g. weekStart=Mon, weekendStart=Sat → 2-day weekend (Sat, Sun)
   *      weekStart=Mon, weekendStart=Thu → 4-day weekend (Thu, Fri, Sat, Sun)
   */
  static isWeekend(date, config) {
    return toUserDay(date.getDay(), config) >= userWeekendStartDay(config);
  }
  /**
   * Checks if two dates fall within the same week.
   * @param date1 First date to check
   * @param date2 Second date to check
   * @param config Week configuration
   * @returns True if both dates are in the same week
   */
  static areDatesInSameWeek(date1, date2, config) {
    const weekStart1 = _WeekUtil.getStartOfWeek(date1, config);
    const weekStart2 = _WeekUtil.getStartOfWeek(date2, config);
    return weekStart1.getTime() === weekStart2.getTime();
  }
  /**
   * Moves a date forward by a number of weeks, keeping the same day of the week.
   * @param date The date to move
   * @param weeks The number of weeks to move forward (positive) or backward (negative)
   * @returns A new date moved by the specified number of weeks
   */
  static moveDateByWeeks(date, weeks) {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + weeks * 7);
    return newDate;
  }
  // ============================================================================
  // "Current week" utilities - special handling for weekends
  // When on a weekend, "this week" means the calendar week containing that weekend
  // ============================================================================
  /**
   * Gets the date of this week's weekend start.
   *
   * - Work day  → days-until-weekend-start (positive offset)
   * - Weekend start day → same day (zero offset)
   * - Last weekend day → previous day (negative offset, back to weekend start)
   *
   * @param now The reference date representing "current" time
   * @param config Week configuration
   * @returns The weekend start date for the current calendar week
   */
  static getThisWeekendStart(now, config) {
    const ud = toUserDay(now.getDay(), config);
    const daysToWeekendStart = userWeekendStartDay(config) - ud;
    const result = new Date(now);
    result.setDate(now.getDate() + daysToWeekendStart);
    result.setHours(0, 0, 0, 0);
    return result;
  }
  /**
   * Gets the date of next week's weekend start (the weekend after this week's weekend).
   * @param now The reference date representing "current" time
   * @param config Week configuration
   * @returns The weekend start date for next week
   */
  static getNextWeekendStart(now, config) {
    const thisWeekendStart = _WeekUtil.getThisWeekendStart(now, config);
    return _WeekUtil.moveDateByWeeks(thisWeekendStart, 1);
  }
  /**
   * Gets the bounds of this week.
   * Uses getThisWeekendStart, so it has special weekend handling.
   *
   * @param now The reference date representing "current" time
   * @param config Week configuration
   * @returns Object with startOfWeek and endOfWeek dates
   */
  static getThisWeekBounds(now, config) {
    const thisWeekendStart = _WeekUtil.getThisWeekendStart(now, config);
    return {
      startOfWeek: _WeekUtil.getStartOfWeek(thisWeekendStart, config),
      endOfWeek: _WeekUtil.getEndOfWeek(thisWeekendStart, config)
    };
  }
  /**
   * Gets the bounds of next week.
   * @param now The reference date representing "current" time
   * @param config Week configuration
   * @returns Object with startOfWeek and endOfWeek dates
   */
  static getNextWeekBounds(now, config) {
    const nextWeekendStart = _WeekUtil.getNextWeekendStart(now, config);
    return {
      startOfWeek: _WeekUtil.getStartOfWeek(nextWeekendStart, config),
      endOfWeek: _WeekUtil.getEndOfWeek(nextWeekendStart, config)
    };
  }
  /**
   * Moves a future date back into the current week, keeping the same day of the week where that
   * still lands on a day the work can be done. Subtracts 7 days at a time until the date is in
   * the current week, then clamps to `now` if that overshot into the past.
   *
   * A date already in the current week, or already past, comes back untouched: the loop never
   * runs, and dragging overdue work forward is not this function's job.
   *
   * @param dateToMove The date to move
   * @param now The reference date representing "current" time
   * @param config Week configuration
   * @returns A new date in the current week, never earlier than `now` unless it started there
   */
  static moveDateToCurrentWeek(dateToMove, now, config) {
    const thisWeekendStart = _WeekUtil.getThisWeekendStart(now, config);
    const endOfThisWeek = _WeekUtil.getEndOfWeek(thisWeekendStart, config);
    let newDate = new Date(dateToMove);
    let moved = false;
    while (newDate > endOfThisWeek) {
      newDate = _WeekUtil.moveDateByWeeks(newDate, -1);
      moved = true;
    }
    return moved && newDate < now ? new Date(now) : newDate;
  }
  /**
   * Checks if a date is in a future week (after the current week).
   * @param date The date to check
   * @param now The reference date representing "current" time
   * @param config Week configuration
   * @returns True if the date is in a future week
   */
  static isDateInFutureWeek(date, now, config) {
    const thisWeekendStart = _WeekUtil.getThisWeekendStart(now, config);
    const endOfThisWeek = _WeekUtil.getEndOfWeek(thisWeekendStart, config);
    return date > endOfThisWeek;
  }
  /**
   * Signed number of whole weeks between the weeks containing `from` and `to`,
   * computed by week-boundary alignment (not raw day diffs). Positive when `to`
   * is in a later week, negative when earlier, 0 when in the same week.
   */
  static weeksBetweenWeekStarts(from, to, config) {
    const fromWeekStart = _WeekUtil.getStartOfWeek(from, config);
    const toWeekStart = _WeekUtil.getStartOfWeek(to, config);
    return Math.round((toWeekStart.getTime() - fromWeekStart.getTime()) / MS_PER_WEEK);
  }
  /**
   * Returns how many whole weeks `taskDate` is before `referenceWeekStart`,
   * computed by week-boundary alignment (not raw day diffs). Always >= 1 — callers
   * should only invoke this for dates known to be overdue.
   */
  static weeksOverdue(taskDate, referenceWeekStart, config) {
    return Math.max(1, _WeekUtil.weeksBetweenWeekStarts(taskDate, referenceWeekStart, config));
  }
};
var MS_PER_WEEK = 7 * 24 * 60 * 60 * 1e3;

// src/homeCalendar.ts
function homeCalendar(home) {
  return { timeZone: homeTimeZone(home), weekConfig: homeWeekConfig(home) };
}
function homeToday(home, now) {
  return todayInTimeZone(now, homeTimeZone(home));
}

// src/homeEvalApplied.ts
function homeEvalApplied(response) {
  return !!response.applyToSpace || (response.applyToEquipmentIds?.length ?? 0) > 0;
}
function homeEvalAppliedSummary(response) {
  if (response.applyToSpace) {
    return "Yes";
  }
  const equipmentCount = response.applyToEquipmentIds?.length ?? 0;
  if (equipmentCount > 0) {
    return `${equipmentCount} equipment`;
  }
  return "No";
}

// src/identifierStrength.ts
var STRONG_IDENTIFIER_MIN_LENGTH = 5;
function identifierStrengthOf(folded) {
  return isStrongIdentifier(folded) ? "strong" : "weak";
}
function isStrongIdentifier(folded) {
  return folded.length >= STRONG_IDENTIFIER_MIN_LENGTH && /[A-Z]/.test(folded) && /\d/.test(folded);
}

// src/inlineTitle.ts
function inlineTitle(title) {
  return title.split(" ").map((word) => word.length > 1 && word === word.toUpperCase() ? word : word.toLowerCase()).join(" ");
}

// src/isHttpUrl.ts
function isHttpUrl(value) {
  if (!value || !/^https?:\/\//i.test(value)) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

// src/testId.ts
var TEST_ID_PREFIX = "test:";
function isTestId(id) {
  return !!id && id.startsWith(TEST_ID_PREFIX);
}
function inheritTestPrefix(parentId, raw) {
  return isTestId(parentId) ? `${TEST_ID_PREFIX}${raw}` : raw;
}

// src/isTestUser.ts
var TEST_USER_EMAIL_PREFIX = "test-user+";
var TEST_USER_EMAIL_PATTERN = /^test-user\+.+@rockhome\.app$/;
var isTestUserEmail = (email) => !!email && TEST_USER_EMAIL_PATTERN.test(email);
var isTestUser = (user) => user.test === true || isTestUserEmail(user.email) || isTestId(user.id);
var isTestHome = (home) => home.test === true || isTestId(home.id) || isTestId(home.owner);

// src/joinWithAnd.ts
function joinWithAnd(items) {
  if (items.length <= 2) return items.join(" and ");
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

// src/linkifyText.ts
function linkifyText(text) {
  const segments = [];
  let cursor = 0;
  for (const match of text.matchAll(LINK_PATTERN)) {
    const start = match.index;
    const raw = match[0];
    const kind = matchKind(match);
    const linkText = kind === "email" || kind === "phone" ? raw : trimUrlPunctuation(raw);
    if (start > cursor) segments.push({ kind: "text", text: text.slice(cursor, start) });
    segments.push(toLink(linkText, kind));
    cursor = start + linkText.length;
  }
  if (cursor < text.length) segments.push({ kind: "text", text: text.slice(cursor) });
  return segments;
}
var URL_CHAR = String.raw`[^\s<>"“”‘’«»‹›—–]`;
var EMAIL = String.raw`[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}`;
var SCHEMED_URL = String.raw`https?:\/\/${URL_CHAR}+`;
var WWW_URL = String.raw`\bwww\.[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/${URL_CHAR}*)?`;
var BARE_DOMAIN = String.raw`\b[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.(?:com|net|org|gov|edu|us|ca|io)\b(?:\/${URL_CHAR}*)?`;
var PHONE = String.raw`(?<![\w-])(?:\+?1[-.\s])?(?:\(\d{3}\)\s?|\d{3}[-.\s])\d{3}[-.\s]\d{4}(?![\w-])`;
var LINK_KINDS = [
  ["email", EMAIL],
  ["url", SCHEMED_URL],
  ["www", WWW_URL],
  ["domain", BARE_DOMAIN],
  ["phone", PHONE]
];
var LINK_PATTERN = new RegExp(LINK_KINDS.map(([, pattern]) => `(${pattern})`).join("|"), "g");
function matchKind(match) {
  const entry = LINK_KINDS.find((_, i) => match[i + 1] !== void 0);
  if (!entry) throw new Error("A link match captures exactly one kind");
  return entry[0];
}
function toLink(text, kind) {
  switch (kind) {
    case "email":
      return { kind: "link", scheme: "mailto", text, href: `mailto:${text}` };
    case "phone":
      return { kind: "link", scheme: "tel", text, href: `tel:${phoneDigits(text)}` };
    case "url":
      return { kind: "link", scheme: "web", text, href: text };
    case "www":
    case "domain":
      return { kind: "link", scheme: "web", text, href: `https://${text}` };
  }
}
function phoneDigits(text) {
  const digits = text.replace(/\D/g, "");
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
}
function trimUrlPunctuation(url) {
  let end = url.length;
  while (end > 0) {
    const last = url.charAt(end - 1);
    if (/[.,;:!?'"]/.test(last)) {
      end--;
    } else if (last === ")" && count(url.slice(0, end), "(") < count(url.slice(0, end), ")")) {
      end--;
    } else {
      break;
    }
  }
  return url.slice(0, end);
}
function count(value, char) {
  return value.split(char).length - 1;
}

// src/mapWithConcurrency.ts
async function mapWithConcurrency(items, limit, fn) {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new RangeError(`mapWithConcurrency: limit must be an integer >= 1, got ${limit}`);
  }
  const results = new Array(items.length);
  const pending = items.entries();
  let failed = false;
  async function worker() {
    for (let step = pending.next(); !step.done && !failed; step = pending.next()) {
      const [index, item] = step.value;
      try {
        results[index] = await fn(item, index);
      } catch (err) {
        failed = true;
        throw err;
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// src/normalizeIdentifier.ts
function normalizeIdentifier(value) {
  const upper = ASCII_ONLY.test(value) ? value.toUpperCase() : foldToUpper(value);
  return upper.replace(/[^A-Z0-9]/g, "");
}
function normalizeNameKey(value) {
  return normalizeIdentifier(upperWithoutAccents(value).replace(/Ø/g, "O"));
}
function foldsToCanonical(value, canonical) {
  return normalizeIdentifier(value) === canonical || normalizeNameKey(value) === canonical;
}
function foldNameplateGlyphs(value) {
  let out = "";
  for (const ch of value) out += NAMEPLATE_GLYPHS[ch] ?? ch;
  return out;
}
function droppedIdentifierChars(value) {
  return [...foldToUpper(value)].filter((ch) => /[\p{L}\p{N}]/u.test(ch) && !/[A-Z0-9]/.test(ch));
}
function foldToUpper(value) {
  return foldNameplateGlyphs(upperWithoutAccents(value));
}
function upperWithoutAccents(value) {
  return value.normalize("NFKC").normalize("NFD").replace(/\p{M}/gu, "").toUpperCase();
}
var ASCII_ONLY = /^[\t\n\r -~]*$/;
var SLASHED_ZEROS = [
  "\xD8",
  // Ø latin capital letter o with stroke
  "\xF8",
  // ø latin small letter o with stroke
  "\u2205",
  // ∅ empty set
  "\u2300",
  // ⌀ diameter sign
  "\u0398",
  // Θ greek capital letter theta
  "\u03B8"
  // θ greek small letter theta
];
var LOOKALIKES = {
  // Cyrillic
  "\u0410": "A",
  // А cyrillic capital letter a
  "\u0412": "B",
  // В cyrillic capital letter ve
  "\u0415": "E",
  // Е cyrillic capital letter ie
  "\u041A": "K",
  // К cyrillic capital letter ka
  "\u041C": "M",
  // М cyrillic capital letter em
  "\u041D": "H",
  // Н cyrillic capital letter en
  "\u041E": "O",
  // О cyrillic capital letter o
  "\u0420": "P",
  // Р cyrillic capital letter er
  "\u0421": "C",
  // С cyrillic capital letter es
  "\u0422": "T",
  // Т cyrillic capital letter te
  "\u0423": "Y",
  // У cyrillic capital letter u
  "\u0425": "X",
  // Х cyrillic capital letter ha
  "\u0406": "I",
  // І cyrillic capital letter byelorussian-ukrainian i
  "\u0408": "J",
  // Ј cyrillic capital letter je
  "\u0405": "S",
  // Ѕ cyrillic capital letter dze
  "\u0430": "a",
  // а cyrillic small letter a
  "\u0435": "e",
  // е cyrillic small letter ie
  "\u043E": "o",
  // о cyrillic small letter o
  "\u0440": "p",
  // р cyrillic small letter er
  "\u0441": "c",
  // с cyrillic small letter es
  "\u0443": "y",
  // у cyrillic small letter u
  "\u0445": "x",
  // х cyrillic small letter ha
  "\u0456": "i",
  // і cyrillic small letter byelorussian-ukrainian i
  "\u0458": "j",
  // ј cyrillic small letter je
  "\u0455": "s",
  // ѕ cyrillic small letter dze
  // Greek
  "\u0391": "A",
  // Α greek capital letter alpha
  "\u0392": "B",
  // Β greek capital letter beta
  "\u0395": "E",
  // Ε greek capital letter epsilon
  "\u0396": "Z",
  // Ζ greek capital letter zeta
  "\u0397": "H",
  // Η greek capital letter eta
  "\u0399": "I",
  // Ι greek capital letter iota
  "\u039A": "K",
  // Κ greek capital letter kappa
  "\u039C": "M",
  // Μ greek capital letter mu
  "\u039D": "N",
  // Ν greek capital letter nu
  "\u039F": "O",
  // Ο greek capital letter omicron
  "\u03A1": "P",
  // Ρ greek capital letter rho
  "\u03A4": "T",
  // Τ greek capital letter tau
  "\u03A5": "Y",
  // Υ greek capital letter upsilon
  "\u03A7": "X",
  // Χ greek capital letter chi
  "\u03BF": "o",
  // ο greek small letter omicron
  // Latin letters with no canonical decomposition
  "\xC6": "AE",
  // Æ latin capital letter ae
  "\xE6": "ae",
  // æ latin small letter ae
  "\u0152": "OE",
  // Œ latin capital ligature oe
  "\u0153": "oe",
  // œ latin small ligature oe
  "\u0141": "L",
  // Ł latin capital letter l with stroke
  "\u0142": "l",
  // ł latin small letter l with stroke
  "\u0110": "D",
  // Đ latin capital letter d with stroke
  "\u0111": "d",
  // đ latin small letter d with stroke
  "\xDE": "TH",
  // Þ latin capital letter thorn
  "\xFE": "th",
  // þ latin small letter thorn
  "\u0131": "i"
  // ı latin small letter dotless i
};
var NAMEPLATE_GLYPHS = {
  ...LOOKALIKES,
  ...Object.fromEntries(SLASHED_ZEROS.map((ch) => [ch, "0"]))
};

// src/meaningfulBrandName.ts
var BRAND_PLACEHOLDERS = /* @__PURE__ */ new Set(["UNKNOWN", "NA", "NONE", "UNBRANDED", "NOTAVAILABLE", "GENERIC"]);
function isMeaningfulBrandName(value) {
  if (!value) return false;
  const key = normalizeNameKey(value);
  return key.length >= 2 && !BRAND_PLACEHOLDERS.has(key);
}

// src/normalizeBrandName.ts
var CORPORATE_SUFFIXES = /* @__PURE__ */ new Set([
  "INC",
  "INCORPORATED",
  "CO",
  "COMPANY",
  "CORP",
  "CORPORATION",
  "LLC",
  "LLP",
  "LTD",
  "LIMITED",
  "SA",
  "AG",
  "GMBH",
  "BV",
  "OY",
  "AB",
  "SPA",
  "PLC",
  "PTY",
  "KK",
  "SAS",
  "SRL",
  "SRO",
  "NV",
  "AS",
  "APS"
]);
function normalizeBrandName(raw) {
  const upper = raw.normalize("NFKC").toUpperCase();
  const collapsed = upper.replace(/[‐-―]/g, "-").replace(/[&'/_.,-]+/g, " ").replace(/\s+/g, " ").trim();
  const tokens = collapsed.split(" ").filter(Boolean);
  while (tokens.length > 1 && CORPORATE_SUFFIXES.has(tokens[tokens.length - 1])) tokens.pop();
  const display = tokens.join(" ");
  return { display, key: normalizeNameKey(display) };
}

// src/ocrConfusion.ts
var OCR_SWAP_PAIRS = [
  ["O", "0"],
  ["I", "1"],
  ["L", "1"],
  ["S", "5"],
  ["B", "8"],
  ["Z", "2"],
  ["G", "6"],
  ["Q", "0"]
];
var CONFUSION_CLASS_REP = {
  O: "0",
  Q: "0",
  D: "0",
  "0": "0",
  I: "1",
  L: "1",
  "1": "1",
  S: "5",
  "5": "5",
  B: "8",
  "8": "8",
  Z: "2",
  "2": "2",
  G: "6",
  "6": "6"
};
function confusionKey(value) {
  const normalized = normalizeIdentifier(value);
  let out = "";
  for (const ch of normalized) {
    out += CONFUSION_CLASS_REP[ch] ?? ch;
  }
  return out;
}
function generateOcrVariants(value) {
  const visited = /* @__PURE__ */ new Set();
  const queue = [value];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === void 0 || visited.has(current)) {
      continue;
    }
    visited.add(current);
    for (const [first, second] of OCR_SWAP_PAIRS) {
      for (const [from, to] of [[first, second], [second, first]]) {
        if (!from) continue;
        if (!current.includes(from)) continue;
        const next = current.replaceAll(from, to);
        if (!visited.has(next)) {
          queue.push(next);
        }
      }
    }
  }
  return visited;
}

// src/orderUrl.ts
var enc = (s) => encodeURIComponent(s);
var RETAILERS = {
  amazon: {
    id: "amazon",
    name: "Amazon",
    domain: "amazon.com",
    brandColor: "#FF9900",
    // Amazon search uses `+` for spaces by convention.
    buildSearchUrl: (q) => `https://www.amazon.com/s?k=${enc(q).replace(/%20/g, "+")}`
  },
  homeDepot: {
    id: "homeDepot",
    name: "The Home Depot",
    domain: "homedepot.com",
    brandColor: "#F96302",
    buildSearchUrl: (q) => `https://www.homedepot.com/s/${enc(q)}`
  },
  lowes: {
    id: "lowes",
    name: "Lowe's",
    domain: "lowes.com",
    brandColor: "#004990",
    buildSearchUrl: (q) => `https://www.lowes.com/search?searchTerm=${enc(q)}`
  }
};
function resolvePurchaseLinks(args) {
  const { type, equipment, amazonAssociateTag } = args;
  const resolved = [];
  for (const link of type.purchaseLinks ?? []) {
    const url = buildPurchaseUrl(link, type, equipment, amazonAssociateTag);
    if (url) {
      resolved.push({ provider: link.provider, url });
    }
  }
  return resolved;
}
function buildPurchaseUrl(link, type, equipment, amazonAssociateTag) {
  if (link.kind === "direct") {
    return link.provider === "amazon" ? withAffiliateTag(link.url, amazonAssociateTag) : link.url;
  }
  const query = resolveTemplate(link.query, type, equipment);
  if (!query) {
    return void 0;
  }
  const url = RETAILERS[link.provider].buildSearchUrl(query);
  return link.provider === "amazon" ? withAffiliateTag(url, amazonAssociateTag) : url;
}
function retailerLogoUrl(domain, brandfetchClientId, size = 128) {
  if (!brandfetchClientId) {
    return void 0;
  }
  return `https://cdn.brandfetch.io/${domain}/w/${size}/h/${size}/icon?c=${encodeURIComponent(brandfetchClientId)}`;
}
function resolveTemplate(template, type, equipment) {
  if (!equipment) {
    return void 0;
  }
  let missing = false;
  const filled = template.replace(/\{([a-zA-Z0-9$:_~-]+)\}/g, (_match, dataKey) => {
    const value = serializeField(dataKey, type, equipment);
    if (!value) {
      missing = true;
      return "";
    }
    return value;
  });
  if (missing) {
    return void 0;
  }
  return filled.trim().replace(/\s+/g, " ");
}
function serializeField(dataKey, type, equipment) {
  const raw = equipment.fields?.[dataKey];
  if (raw === void 0 || raw === null || raw === "") {
    return void 0;
  }
  const field = type.inputFields?.find((f) => f.dataKey === dataKey);
  if (field?.dataType === "dimensions" && typeof raw === "string") {
    const parsed = DimensionValueUtil.parseDimension(
      raw,
      field.dimensionsSubType ?? "LxWxH",
      field.defaultLengthSelection
    );
    const parts = parsed?.parts.filter(Boolean) ?? [];
    return parts.length ? parts.join("x") : void 0;
  }
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    return `${raw}`;
  }
  return void 0;
}
function withAffiliateTag(url, tag) {
  if (!tag || /[?&]tag=/.test(url)) {
    return url;
  }
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}tag=${encodeURIComponent(tag)}`;
}

// src/pluralize.ts
function pluralize(singular, countOrPlural, maybeCount) {
  const count2 = typeof countOrPlural === "number" ? countOrPlural : maybeCount;
  const customPlural = typeof countOrPlural === "string" ? countOrPlural : void 0;
  if (count2 === 1) {
    return singular;
  }
  if (customPlural !== void 0) {
    return customPlural;
  }
  const lower = singular.toLowerCase();
  const isAllUpperCase = singular === singular.toUpperCase() && singular !== singular.toLowerCase();
  const matchCase = (suffix) => isAllUpperCase ? suffix.toUpperCase() : suffix;
  if (lower.endsWith("ss") || lower.endsWith("x") || lower.endsWith("z") || lower.endsWith("sh") || lower.endsWith("ch")) {
    if (lower.endsWith("z") && !lower.endsWith("zz")) {
      return `${singular}${matchCase("zes")}`;
    }
    return `${singular}${matchCase("es")}`;
  }
  if (lower.endsWith("o") && singular.length > 1) {
    const beforeO = lower[lower.length - 2];
    if (beforeO && !"aeiou".includes(beforeO)) {
      return `${singular}${matchCase("es")}`;
    }
  }
  if (lower.endsWith("y") && singular.length > 1) {
    const beforeY = lower[lower.length - 2];
    if (beforeY && !"aeiou".includes(beforeY)) {
      return `${singular.slice(0, -1)}${matchCase("ies")}`;
    }
  }
  if (lower.endsWith("s") && !lower.endsWith("ss")) {
    return singular;
  }
  return `${singular}${matchCase("s")}`;
}

// src/rebaseTextEdit.ts
function rebaseTextEdit(current, base, target) {
  if (base === target) return current;
  const edit = computeEdit(base, target);
  const matches = lcsMatches(base, current);
  if (edit.kind === "insert") {
    const pos = mapInsertPosition(matches, edit.basePos, base.length, current.length);
    return current.slice(0, pos) + edit.content + current.slice(pos);
  }
  const range = mapDeleteRange(matches, edit.basePos, edit.length);
  if (!range) return current;
  return current.slice(0, range.start) + current.slice(range.end);
}
function computeEdit(base, target) {
  let prefix = 0;
  while (prefix < base.length && prefix < target.length && base[prefix] === target[prefix]) {
    prefix++;
  }
  let suffix = 0;
  while (suffix < base.length - prefix && suffix < target.length - prefix && base[base.length - 1 - suffix] === target[target.length - 1 - suffix]) {
    suffix++;
  }
  const baseChunk = base.slice(prefix, base.length - suffix);
  const targetChunk = target.slice(prefix, target.length - suffix);
  if (baseChunk === "" && targetChunk !== "") {
    return { kind: "insert", basePos: prefix, content: targetChunk };
  }
  if (targetChunk === "" && baseChunk !== "") {
    return { kind: "delete", basePos: prefix, length: baseChunk.length };
  }
  throw new Error(
    `rebaseTextEdit: expected pure insert or delete, got ${JSON.stringify(base)} \u2192 ${JSON.stringify(target)}`
  );
}
function lcsMatches(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  for (let i2 = 1; i2 <= m; i2++) {
    for (let j2 = 1; j2 <= n; j2++) {
      if (a[i2 - 1] === b[j2 - 1]) {
        dp[i2][j2] = dp[i2 - 1][j2 - 1] + 1;
      } else {
        dp[i2][j2] = Math.max(dp[i2 - 1][j2], dp[i2][j2 - 1]);
      }
    }
  }
  const matches = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      matches.push([i - 1, j - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  matches.reverse();
  return matches;
}
function mapInsertPosition(matches, basePos, baseLen, currentLen) {
  if (basePos === baseLen) return currentLen;
  let before = null;
  let after = null;
  for (const [i, j] of matches) {
    if (i < basePos) {
      before = { base: i, current: j };
    } else if (after === null) {
      after = { base: i, current: j };
    }
  }
  if (before === null && after === null) return currentLen;
  if (before === null) return after.current;
  if (after === null) return before.current + 1;
  const distFromBefore = basePos - before.base;
  const distToAfter = after.base - basePos;
  return distFromBefore <= distToAfter ? before.current + 1 : after.current;
}
function mapDeleteRange(matches, baseStart, length) {
  const baseEnd = baseStart + length;
  const currentPositions = [];
  for (const [i, j] of matches) {
    if (i >= baseStart && i < baseEnd) currentPositions.push(j);
  }
  if (currentPositions.length === 0) return null;
  const start = Math.min(...currentPositions);
  const end = Math.max(...currentPositions) + 1;
  return { start, end };
}

// src/rotation.ts
function normalizeRotation(deg) {
  if (!deg) return 0;
  return (deg % 360 + 360) % 360;
}
function isQuarterTurn(deg) {
  const normalized = normalizeRotation(deg);
  return normalized === 90 || normalized === 270;
}

// src/shallowEqual.ts
var hasOwnProperty = Object.prototype.hasOwnProperty;
function is(x, y) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    return x !== x && y !== y;
  }
}
function shallowEqual(objA, objB) {
  if (is(objA, objB)) {
    return true;
  }
  if (typeof objA !== "object" || objA === null || typeof objB !== "object" || objB === null) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (let i = 0; i < keysA.length; i++) {
    if (!hasOwnProperty.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }
  return true;
}

// src/slugify.ts
function slugify(text, fallback = "item") {
  const slug = text.toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+/, "").slice(0, 50).replace(/-+$/g, "");
  return slug || fallback;
}

// src/inviteCode.ts
var INVITE_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
var INVITE_CODE_LENGTH = 8;
function generateInviteCode(random) {
  let code = "";
  for (let i = 0; i < INVITE_CODE_LENGTH; i++) {
    code += INVITE_CODE_ALPHABET[Math.floor(random() * INVITE_CODE_ALPHABET.length)];
  }
  return code;
}
function normalizeInviteCode(input) {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function isInviteCode(normalized) {
  return normalized.length === INVITE_CODE_LENGTH && [...normalized].every((c) => INVITE_CODE_ALPHABET.includes(c));
}
function formatInviteCode(input) {
  const normalized = input.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, INVITE_CODE_LENGTH);
  return normalized.length > 4 ? `${normalized.slice(0, 4)}-${normalized.slice(4)}` : normalized;
}
var INVITE_LINK_PREFIX = "/homes/invite/";
function inviteLinkPath(code) {
  return `${INVITE_LINK_PREFIX}${formatInviteCode(code)}`;
}
function isInviteLinkPath(path) {
  return path.startsWith(INVITE_LINK_PREFIX);
}

// src/stripDetectedLabel.ts
var CODE_LABEL_PREFIXES = ["MODEL", "MOD", "M/N", "MN", "TYPE", "CATALOG", "CAT", "SERIAL", "SER", "S/N", "SN"];
var LABEL_PREFIX_RE = new RegExp(`^(?:${CODE_LABEL_PREFIXES.join("|")})\\s*(?:NO\\.?|NUMBER|#)?\\s*[:#=]\\s*`, "i");
function stripDetectedLabel(text) {
  const stripped = text.replace(LABEL_PREFIX_RE, "");
  return stripped.length > 0 ? stripped : text;
}

// src/typeAffinity.ts
import { isCustomId } from "@rock/types";
function typeAffinity(typeId, typeIds, families) {
  if (typeIds.includes(typeId)) return "type";
  const family = familyOf(typeId, families);
  return family && typeIds.some((listed) => family.typeIds.includes(listed)) ? "family" : void 0;
}
function familyOf(typeId, families) {
  if (isCustomId(typeId)) return void 0;
  return families.find((family) => family.typeIds.includes(typeId));
}
function familyReach(typeIds, families) {
  return families.flatMap((family) => {
    if (!typeIds.some((id) => familyOf(id, [family]))) return [];
    const others = family.typeIds.filter((id) => !typeIds.includes(id));
    return others.length > 0 ? [{ family, others }] : [];
  });
}

// src/unitBrand.ts
function unitBrandOf(unit) {
  const owner = unit.fields?.brand?.trim();
  if (isMeaningfulBrandName(owner)) return { name: owner, source: "owner" };
  const product = unit.details?.modelId ? unit.details.brand?.trim() : void 0;
  return isMeaningfulBrandName(product) ? { name: product, source: "product" } : void 0;
}
function unitBrandName(unit) {
  return unitBrandOf(unit)?.name;
}

// src/upcomingBucket.ts
var UPCOMING_BUCKETS = [
  "this weekend",
  "in the next few weeks",
  "in the next month or two",
  "later this year"
];
function describeUpcomingBucket(date, now) {
  const days = daysBetweenLocalDates(date, anchorDay(now));
  if (days <= 7) return "this weekend";
  if (days <= 28) return "in the next few weeks";
  if (days <= 70) return "in the next month or two";
  return "later this year";
}
function describeLaterBucket(date, weekEnd) {
  const bucket = describeUpcomingBucket(date, weekEnd);
  if (bucket === "this weekend") {
    return "next weekend";
  }
  if (bucket === "later this year" && date.slice(0, 4) > anchorDay(weekEnd).slice(0, 4)) {
    return "next year";
  }
  return bucket;
}
function anchorDay(now) {
  return typeof now === "string" ? toLocalDate(now) : todayLocalDate(now);
}

// src/visibleCoreFields.ts
function visibleCoreFields(fields, context) {
  return fields.filter(
    (f) => f.enabled !== false && inScope(f, context.scope) && (!f.equipmentTypeIds || !!context.typeId && f.equipmentTypeIds.includes(context.typeId))
  );
}
function fieldsForEquipment(fields, context, values) {
  const visible = visibleCoreFields(fields, context);
  const heldByTypeList = fields.filter(
    (f) => !!f.equipmentTypeIds && !visible.includes(f) && f.enabled !== false && inScope(f, context.scope) && hasValue(values?.[f.dataKey])
  );
  return [...visible, ...heldByTypeList];
}
function inScope(field, scope) {
  return !field.equipmentScopes || field.equipmentScopes.includes(scope);
}
function hasValue(value) {
  if (value === void 0 || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

// src/weekendReviews.ts
import { LocalDateSchema as LocalDateSchema2 } from "@rock/types";
var REVIEW_QUERY_START = LocalDateSchema2.parse("1970-01-01");
function selectWeekendReviews(reviews, range, { isOpen, isResolvedInRange = () => false }) {
  const carried = reviews.filter((r) => r.reviewAt < range.start && (isOpen(r) || isResolvedInRange(r)));
  const inRange = reviews.filter((r) => isLocalDateInRange(r.reviewAt, range));
  return [...carried.sort(byReviewAt), ...inRange.sort(byReviewAt)];
}
function hasReviewAt(item) {
  return !!item.reviewAt;
}
function byReviewAt(a, b) {
  return compareLocalDates(a.reviewAt, b.reviewAt);
}
export {
  DATE_GROUP_LABELS,
  DAYS_OF_WEEK,
  DEFAULT_MAINTENANCE_MESSAGE,
  DEFAULT_UPGRADE_MESSAGE,
  DEFAULT_WEEKEND_START,
  DEFAULT_WEEK_CONFIG,
  DimensionValueUtil,
  FALLBACK_TIME_ZONE,
  INVITE_CODE_ALPHABET,
  INVITE_CODE_LENGTH,
  OCR_SWAP_PAIRS,
  RETAILERS,
  REVIEW_QUERY_START,
  STRONG_IDENTIFIER_MIN_LENGTH,
  TEST_ID_PREFIX,
  TEST_USER_EMAIL_PATTERN,
  TEST_USER_EMAIL_PREFIX,
  UPCOMING_BUCKETS,
  WeekUtil,
  addDaysToLocalDate,
  addIntervalToLocalDate,
  assertUnreachable,
  cleanFirestoreId,
  compareLocalDates,
  computeTimeSince,
  computeTimeSinceDay,
  confusionKey,
  considerationCadence,
  considerationDemandClass,
  dateToLocalDate,
  daysBetweenLocalDates,
  dedupeStrings,
  describeLaterBucket,
  describeUpcomingBucket,
  detectCodeLabels,
  droppedIdentifierChars,
  equipmentIdentity,
  equipmentModelNumber,
  errInfo,
  evaluateFieldDisplay,
  familyOf,
  familyReach,
  fieldsForEquipment,
  filterNonNull,
  firestoreErrorCode,
  foldNameplateGlyphs,
  foldsToCanonical,
  formatGuideValue,
  formatInviteCode,
  formatLocalDateLong,
  formatLocalDateMonthYear,
  formatLocalDateShort,
  formatLocalDateWithWeekday,
  generateInviteCode,
  generateOcrVariants,
  getDimensionFieldCount,
  getLocalParts,
  groupByDate,
  hasActionableTaskTypes,
  hasReviewAt,
  hideProductNameBesideModelNumber,
  homeCalendar,
  homeEvalApplied,
  homeEvalAppliedSummary,
  homeTimeZone,
  homeToday,
  homeWeekConfig,
  identifierStrengthOf,
  inheritTestPrefix,
  inlineTitle,
  inviteLinkPath,
  isActNowCriticality,
  isActionableConsideration,
  isAnsweredSince,
  isFirestoreCode,
  isHttpUrl,
  isInviteCode,
  isInviteLinkPath,
  isLocalDateInRange,
  isMeaningfulBrandName,
  isOnThisWeek,
  isPendingThisWeek,
  isPushedBackByOwner,
  isQuarterTurn,
  isQueued,
  isScheduledForLater,
  isStrongIdentifier,
  isTestHome,
  isTestId,
  isTestUser,
  isTestUserEmail,
  isTransientReadError,
  isUnreadThisWeek,
  isVersionBelow,
  joinWithAnd,
  knownTimeZone,
  linkifyText,
  liveTaskTypes,
  localDayStartIso,
  localWallClockToIso,
  makeLocalAnchorDate,
  mapWithConcurrency,
  nextLocalSendAt,
  nextWeekendStartLocalDate,
  normalizeBrandName,
  normalizeIdentifier,
  normalizeInviteCode,
  normalizeNameKey,
  normalizeRotation,
  parseFlexibleDate,
  parseLocalDate,
  parseManufactureDate,
  parseVersionParts,
  pluralize,
  rebaseTextEdit,
  resolveAlertBanner,
  resolveAppStatusGates,
  resolvePurchaseLinks,
  resolveReviewStamp,
  retailerLogoUrl,
  selectWeekendReviews,
  shallowEqual,
  slugify,
  sortScheduledForLaterLast,
  stripDetectedLabel,
  toLocalDate,
  toLocalDateRange,
  todayInTimeZone,
  todayLocalDate,
  typeAffinity,
  uniqueById,
  unitBrandName,
  unitBrandOf,
  visibleCoreFields,
  weekBoundsToLocalDateRange,
  weekRangeContaining,
  weekendAtLeastDaysOut,
  weekendOnOffer
};
//# sourceMappingURL=index.js.map